import React, { useState, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ChevronRight, Filter, Upload, X, Image, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { products, categories } from '../data/mockData';

// Extended products for the shop page
const allProducts = [
  ...products,
  {
    id: 5,
    name: "Hoodie",
    description: "Premium heavyweight hoodie, hand-pressed with vibrant, long-lasting color. Stay warm in style.",
    price: 55.00,
    category: "apparel",
    image: "https://royal-ink-shop.lovable.app/assets/emperor-hoodie-CGfXIoPu.png",
    customizable: true
  },
  {
    id: 6,
    name: "Custom Mug",
    description: "Premium ceramic mug with UVDTF printing. Perfect for your morning coffee.",
    price: 18.00,
    category: "drinkware",
    image: "https://royal-ink-shop.lovable.app/assets/uvdtf-mug-CKugI6ad.jpg",
    customizable: true
  },
  {
    id: 7,
    name: "Ballcap",
    description: "Premium baseball cap with embroidered or printed design. Classic snapback style for any occasion.",
    price: 28.00,
    category: "accessories",
    image: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=600&h=400&fit=crop",
    customizable: true
  },
  {
    id: 8,
    name: "Skullcap / Beanie",
    description: "Cozy knit beanie with custom embroidery options. Perfect for cooler weather.",
    price: 22.00,
    category: "accessories",
    image: "https://images.unsplash.com/photo-1576871337632-b9aef4c17ab9?w=600&h=400&fit=crop",
    customizable: true
  },
  {
    id: 9,
    name: "Canvas Tote Bag",
    description: "Durable canvas tote bag with custom print. Eco-friendly and stylish for everyday use.",
    price: 25.00,
    category: "accessories",
    image: "https://images.unsplash.com/photo-1548863227-3af567fc3b27?w=600&h=400&fit=crop",
    customizable: true
  },
  {
    id: 10,
    name: "Emperor Collection Tee",
    description: "Exclusive Royal Wear collection. Premium cotton with signature Emperor design.",
    price: 40.00,
    category: "royal-wear",
    image: "https://royal-ink-shop.lovable.app/assets/apparel-mockup-1-CpSHoLEe.png",
    customizable: false
  }
];

// Custom Order Form Component with Artwork Uploader
const CustomOrderSection = () => {
  const fileInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    productType: '',
    quantity: '',
    description: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2),
      preview: URL.createObjectURL(file)
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-serif text-2xl font-semibold text-gray-900 mb-2">
          Custom Order Submitted!
        </h3>
        <p className="text-gray-500 mb-6">
          We'll review your artwork and get back to you within 24-48 hours with a quote.
        </p>
        <Button 
          onClick={() => {
            setSubmitted(false);
            setUploadedFiles([]);
            setFormData({ name: '', email: '', phone: '', productType: '', quantity: '', description: '' });
          }}
          className="bg-amber-500 hover:bg-amber-600 text-gray-900"
        >
          Submit Another Order
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div 
        className="relative rounded-xl overflow-hidden h-48 md:h-64"
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_artisan-ink/artifacts/bhivj209_Gemini_Generated_Image_50jebd50jebd50je.png" 
          alt="Custom Printing" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
        <div className="absolute inset-0 flex items-center p-6 md:p-10">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-3">
              Custom <span className="text-amber-400 italic">Printing</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-xl">
              Upload your artwork and we'll bring your vision to life with premium quality printing.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Order Form */}
      <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
        <h3 className="font-serif text-xl font-semibold text-gray-900 mb-6">
          Submit Your Custom Order
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Artwork Uploader */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Your Artwork *
            </label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/30 transition-all"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.ai,.psd,.eps"
                onChange={handleFileChange}
                className="hidden"
              />
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">
                Click to upload or drag and drop
              </p>
              <p className="text-gray-400 text-sm mt-1">
                PNG, JPG, PDF, AI, PSD, EPS (Max 25MB)
              </p>
            </div>

            {/* Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{file.size} MB</p>
                    </div>
                    <button 
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(412) 555-0123"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Type *</label>
              <Input
                name="productType"
                value={formData.productType}
                onChange={handleChange}
                required
                placeholder="T-shirts, Hoodies, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Quantity *</label>
              <Input
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                required
                placeholder="e.g., 25"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Project Details</label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Tell us about your project - colors, sizes, placement, deadline, etc."
            />
          </div>

          <Button 
            type="submit"
            className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 py-6 font-semibold rounded-lg"
          >
            <Image className="mr-2 h-4 w-4" />
            Submit Custom Order
          </Button>
        </form>
      </div>
    </div>
  );
};

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const [showFilters, setShowFilters] = useState(false);

  const filteredProducts = useMemo(() => {
    if (!categoryFilter) return allProducts;
    return allProducts.filter(p => p.category === categoryFilter);
  }, [categoryFilter]);

  const handleCategoryClick = (slug) => {
    if (slug === categoryFilter) {
      setSearchParams({});
    } else {
      setSearchParams({ category: slug });
    }
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const isCustomCategory = categoryFilter === 'custom';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">Shop</span>
            {categoryFilter && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900 font-medium capitalize">
                  {categoryFilter.replace('-', ' ')}
                </span>
              </>
            )}
          </nav>

          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-gray-900">
                {categoryFilter 
                  ? categories.find(c => c.slug === categoryFilter)?.name || 'Products'
                  : 'All Products'
                }
              </h1>
              {!isCustomCategory && (
                <p className="text-gray-500 mt-2">
                  {filteredProducts.length} products
                </p>
              )}
            </div>
            
            <Button
              variant="outline"
              className="md:hidden flex items-center gap-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Categories</h3>
                  {categoryFilter && (
                    <button 
                      onClick={clearFilters}
                      className="text-amber-600 text-sm hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => handleCategoryClick('')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        !categoryFilter 
                          ? 'bg-amber-50 text-amber-700 font-medium' 
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      All Products
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <button
                        onClick={() => handleCategoryClick(cat.slug)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          categoryFilter === cat.slug 
                            ? 'bg-amber-50 text-amber-700 font-medium' 
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Products Grid or Custom Order Form */}
            <div className="flex-1">
              {isCustomCategory ? (
                <CustomOrderSection />
              ) : filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-gray-500 text-lg">No products found in this category.</p>
                  <Button 
                    onClick={clearFilters}
                    className="mt-4 bg-amber-500 hover:bg-amber-600 text-gray-900"
                  >
                    View All Products
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
